<template>
    <table v-bind="$attrs">
        <thead>
            <slot
                :headers="headers"
                :sort="sort"
                name="header"
            >
                <tr>
                    <th
                        v-for="(header, column) in headers"
                        :key="column"
                        @click="sort(column)"
						:class="{ 'sortable': true, 'active-sort': currentSortCriteria.some(c => c.column === column) }"
                    >
                        {{ header.text }}
						<span v-if="getSortIndicator(column)">
                            {{ getSortIndicator(column) }}
                            <small v-if="multiSortEnabled && currentSortCriteria.length > 1">({{ getSortOrderNumber(column) }})</small>
                        </span>
                    </th>
                </tr>
            </slot>
        </thead>
        <tbody v-if="displayedItems.length">
            <slot
                :headers="headers"
                :displayedItems="displayedItems"
                name="body"
            >
                <tr
                    v-for="(item, row) in displayedItems"
                    :key="row"
                >
                    <td
                        v-for="(header, column) in headers"
                        :key="column"
                    >
                        {{ item[header.value] }}
                    </td>
                </tr>
            </slot>
        </tbody>
        <tbody v-else>
            <tr>
                <td :colspan="headers.length">
                    <slot
                        :search="search"
                        name="no-results"
                    >
                        No results.
                    </slot>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';

export default {
    props: {
        headers: {
            type: Array,
            required: true,
        },
        items: {
            type: Array,
            required: true,
        },
        search: {
            type: String,
            default: '',
        },
        itemsPerPage: {
            type: Number,
            default: 9999,
        },
        page: {
            type: Number,
            default: 1,
        },
		sortColumnDefault: {
			type: [String, Number],
			required: false,
		},
		sortReverseDefault: {
            type: Boolean,
            required: false,
        },
		ignoreEmptySort: {
            type: Boolean,
            default: false,
        },
		emptyValuesToBottom: {
            type: Boolean,
            default: true,
        },
		multiSortDefault: {
        	type: Array, 
    	    default: () => [],
	        validator: (value) => value.every(item => typeof item === 'object' && 'column' in item && 'reverse' in item && typeof item.column === 'string' && typeof item.reverse === 'boolean'),
		},
		multiSortEnabled: {
			type: Boolean,
			default: false,
		},
    },
    data() {
        return {
            sortColumn: this.sortColumnDefault ? this.sortColumnDefault : undefined,
            sortReverse: this.sortReverseDefault ? this.sortReverseDefault : false,
			currentSortCriteria: [],
        };
    },
	created() {
		if (this.multiSortDefault && this.multiSortDefault.length > 0 && this.multiSortEnabled) {
			this.currentSortCriteria = this.multiSortDefault.map(criteria => {
				const columnIndex = this.headers.findIndex(h => h.value === criteria.column);
				if (columnIndex === -1) {
					console.warn(`Multi-sort: Column '${criteria.column}' not found in headers.`);
					return null;
				}
				return { column: columnIndex, reverse: criteria.reverse };
			}).filter(Boolean);
		} else if (this.sortColumnDefault !== undefined && this.sortColumnDefault !== null && this.sortColumnDefault !== '') {
			const columnIndex = parseInt(this.sortColumnDefault, 10);

			if (!isNaN(columnIndex) && columnIndex >= 0 && columnIndex < this.headers.length) {
				this.currentSortCriteria = [{
					column: columnIndex,
					reverse: this.sortReverseDefault || false,
				}];
			} else {
				console.warn(`Single-sort: Invalid sortColumnDefault index '${this.sortColumnDefault}'. Must be a valid header index.`);
			}
		}
	},
    computed: {
        displayedItems() {
            return this.paginatedItems;
        },
        offset() {
            return (this.page - 1) * this.itemsPerPage;
        },
        pages() {
            return Math.ceil(this.displayedItems.length / this.itemsPerPage);
        },
        searchTokens() {
            return this.search.split(/\s+/).filter((token) => token);
        },
        searchedItems() {
            return _.filter(this.items, (item) => {
                const matchedFields = _.filter(item, (field) => {
                    const matchedTokens = _.filter(this.searchTokens, (token) => {
                        const pattern = new RegExp(token, 'i');

                        return String(field !== null && field !== undefined ? field : '').search(pattern) > -1;
                    });

                    return matchedTokens.length === this.searchTokens.length;
                });

                return matchedFields.length > 0;
            });
        },
        sortValue() {
            return this.headers[this.sortColumn].value;
        },
		sortedItems() {
			if (this.currentSortCriteria.length === 0) {
				return this.searchedItems;
			}

			const primarySortCriterion = this.currentSortCriteria[0];
			const primarySortValueKey = primarySortCriterion ? this.headers[primarySortCriterion.column].value : null;

			let itemsWithValue = [];
			let itemsWithoutValue = [];

			if (this.ignoreEmptySort && primarySortValueKey) {
				this.searchedItems.forEach(item => {
					const value = item[primarySortValueKey];
					if (value === null || value === undefined || value === '') {
						itemsWithoutValue.push(item);
					} else {
						itemsWithValue.push(item);
					}
				});
			} else {
				itemsWithValue = [...this.searchedItems];
			}

			let sortedItemsWithValue = itemsWithValue;

			if (itemsWithValue.length > 0) {
				const collator = new Intl.Collator('pl', { usage: 'sort', sensitivity: 'accent' });
				const compare = (a, b) => {
					for (const criteria of this.currentSortCriteria) {
						const header = this.headers[criteria.column];
						const sortValueKey = header.value;

						const va = a[sortValueKey];
						const vb = b[sortValueKey];

						const aEmpty = va === null || va === undefined || va === '';
						const bEmpty = vb === null || vb === undefined || vb === '';

						if (aEmpty && bEmpty) continue;
						if (aEmpty) return criteria.reverse ? 1 : -1;
						if (bEmpty) return criteria.reverse ? -1 : 1;

						let result = 0;

						if (header.type === 'date' || (_.isString(va) && String(va).match(/^\d\d\.\d\d\.\d\d\d\d/))) { // Added anchor to regex
							const ta = moment(va, 'DD.MM.YYYY hh:mm').unix();
							const tb = moment(vb, 'DD.MM.YYYY hh:mm').unix();
							result = ta === tb ? 0 : (ta < tb ? -1 : 1);
						} else if (sortValueKey === 'payment') {
							const na = parseFloat(String(va).replace(' PLN', ''));
							const nb = parseFloat(String(vb).replace(' PLN', ''));
							result = na === nb ? 0 : (na < nb ? -1 : 1);
						} else if (_.isString(va) && _.isString(vb)) {
							result = collator.compare(String(va), String(vb));
						} else {
							if (va === vb) {
								result = 0;
							} else {
								result = va < vb ? -1 : 1;
							}
						}

						if (result !== 0) {
							return criteria.reverse ? -result : result;
						}
					}
					return 0;
				};

				sortedItemsWithValue = [...itemsWithValue].sort(compare);
			} else {
				sortedItemsWithValue = []; // No items to sort
			}


			if (this.ignoreEmptySort) {
				return this.emptyValuesToBottom
					? [...sortedItemsWithValue, ...itemsWithoutValue]
					: [...itemsWithoutValue, ...sortedItemsWithValue];
			} else {
				return sortedItemsWithValue;
			}
		},
        paginatedItems() {
            return _.slice(
                this.sortedItems,
                this.offset,
                this.offset + this.itemsPerPage,
            );
        },
    },
    watch: {
        search() {
            this.$emit('update:page', 1);
        },
    },
    methods: {
		sort(column) {
			const columnIndex = column;

			if (this.multiSortEnabled) {
				const existingCriterionIndex = this.currentSortCriteria.findIndex(c => c.column === columnIndex);

				if (existingCriterionIndex !== -1) {
					if (!this.currentSortCriteria[existingCriterionIndex].reverse) {
						this.currentSortCriteria[existingCriterionIndex].reverse = true;
					} else {
						this.currentSortCriteria.splice(existingCriterionIndex, 1);
					}
				} else {
					this.currentSortCriteria.unshift({ column: columnIndex, reverse: false });
				}

			} else {
				const isCurrentColumn = this.currentSortCriteria.length > 0 && this.currentSortCriteria[0].column === columnIndex;
				const currentReverse = isCurrentColumn ? this.currentSortCriteria[0].reverse : false;

				if (isCurrentColumn) {
					this.currentSortCriteria[0].reverse = !currentReverse;
				} else {
					this.currentSortCriteria = [{ column: columnIndex, reverse: false }];
				}
			}

			this.$emit('update-sort', this.currentSortCriteria.map(c => ({
				column: this.headers[c.column].value,
				reverse: c.reverse,
			})));
		},
		getSortIndicator(column) {
            const criterion = this.currentSortCriteria.find(c => c.column === column);
            if (criterion) {
                return criterion.reverse ? '🔽' : '🔼';
            }
            return '';
        },
        getSortOrderNumber(column) {
            const index = this.currentSortCriteria.findIndex(c => c.column === column);
            return index !== -1 ? (index + 1) : '';
        }
    },
};
</script>
